import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Table, Typography, Row, Col, Select, Button, Spin } from "antd";
import ProbationReviewModal from "./ProbationReviewModal";

const { Title } = Typography;
const { Option } = Select;

export default function ProbationReview() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [searchEmpCode, setSearchEmpCode] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });


  const { backUrl, authData } = useContext(AuthContext);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [Organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [organisationFilter, setOrganisationFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");

  const api = "/api/probation/probation-reviews/?is_employee_view=true";
  const hrapi = "/api/probation/probation-reviews/?is_employee_view=true";

  // FETCH ORGS
  const fetchOrganisation = async () => {
    const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    });
    setOrganisations(res.data.results || []);
  };

  // FETCH DEPARTMENTS BASED ON ORG ID
  const fetchDepartments = async (orgId) => {
    const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    });
    setDepartments(res.data.results || []);
  };

  // FETCH DESIGNATIONS BASED ON DEPT ID
  const fetchDesignations = async (deptId) => {
    const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    });
    setDesignations(res.data.results || []);
  };

  useEffect(() => {
    fetchOrganisation();
  }, []);

  // FETCH EMPLOYEES
  const fetchData = async () => {
    setLoading(true);
    try {
      let url = authData?.employee?.is_superuser
        ? `${backUrl}${hrapi}`
        : `${backUrl}${api}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });

      setEmployees(res.data.results || []);
      setFilteredEmployees(res.data.results || []);
    } catch (error) {
      console.error("Error fetching probation reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // -----------------------------
  // ⭐ FILTER LOGIC (AUTO APPLY)
  // -----------------------------
  useEffect(() => {
    let filtered = [...employees];

    if (organisationFilter) {
      filtered = filtered.filter(
        (item) => item.employee?.organisation_name === organisationFilter
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter(
        (item) => item.employee?.department_name === departmentFilter
      );
    }

    if (designationFilter) {
      filtered = filtered.filter(
        (item) => item.employee?.designation_name === designationFilter
      );
    }

    if (searchEmpCode) {
      filtered = filtered.filter((item) =>
        item.employee?.emp_code
          ?.toLowerCase()
          .includes(searchEmpCode.toLowerCase())
      );
    }

    if (searchName) {
      filtered = filtered.filter((item) => {
        const fullName =
          `${item.employee?.first_name} ${item.employee?.last_name}`.toLowerCase();
        return fullName.includes(searchName.toLowerCase());
      });
    }

    setPagination((prev) => ({ ...prev, current: 1 }));

    setFilteredEmployees(filtered);
  }, [organisationFilter,
    departmentFilter,
    designationFilter,
    searchName,
    searchEmpCode,
    employees,]);

  // -----------------------------
  // ⭐ CLEAR FILTERS
  // -----------------------------
  const handleClearFilters = () => {
    setSearchName("");
    setSearchEmpCode("");
    setOrganisationFilter("");
    setDepartmentFilter("");
    setDesignationFilter("");
    setDepartments([]);
    setDesignations([]);
    setFilteredEmployees(employees);
    setPagination({ current: 1, pageSize: 5 });
  };
  // -----------------------------


  const columns = [
    {
      title: "Emp Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      render: (text, record) => (
        <a
          onClick={() => {
            setSelectedEmployee(record);
            setShowModal(true);
          }}
          style={{ cursor: "pointer" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Employee Name",
      key: "employee_name",
      render: (_, record) =>
        `${record.employee_name}`,
    },
    {
      title: "Organisation",
      dataIndex: ["employee", "organisation_name"],
      key: "organisation",
    },
    {
      title: "Department",
      dataIndex: ["employee", "department_name"],
      key: "department",
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation_name"],
      key: "designation",
    },
    {
      title: "Date of Joining",
      dataIndex: ["employee", "date_of_joining"],
      key: "date_of_joining",
      render: (text) => new Date(text).toLocaleDateString("en-GB"),
    },
    {
      title: "Probation End Date",
      dataIndex: "final_probation_end_date",
      key: "final_probation_end_date",
      render: (text) => new Date(text).toLocaleDateString("en-GB"),
    },
  ];

  return (
    <>
      <center>
        <Title level={4}>Team Employment Status</Title>
      </center>

      {/* FILTER ROW */}
      <Row gutter={[10, 10]} style={{ marginBottom: 15 }}>

        {/* Employee Code Search */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <input
            type="text"
            placeholder="Search Emp Code"
            value={searchEmpCode}
            onChange={(e) => setSearchEmpCode(e.target.value)}
            style={{
              width: "100%",
              height: "32px",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
            }}
          />
        </Col>

        {/* Employee Name Search */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <input
            type="text"
            placeholder="Search Name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{
              width: "100%",
              height: "32px",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
            }}
          />
        </Col>


        {/* Organisation */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Select Organisation"
            style={{ width: "100%" }}
            value={organisationFilter || undefined}
            allowClear
            onChange={(value) => {
              setOrganisationFilter(value);
              setDepartmentFilter("");
              setDesignationFilter("");

              setDepartments([]);
              setDesignations([]);

              if (value) {
                const orgObj = Organisations.find((o) => o.name === value);
                if (orgObj) fetchDepartments(orgObj.id);
              }
            }}
          >
            {Organisations.map((org) => (
              <Option key={org.id} value={org.name}>
                {org.name}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Department */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            showSearch
            placeholder="Select Department"
            style={{ width: "100%" }}
            value={departmentFilter || undefined}
            allowClear
            disabled={!organisationFilter}
            onChange={(value) => {
              setDepartmentFilter(value);
              setDesignationFilter("");
              setDesignations([]);

              const deptObj = departments.find((d) => d.name === value);
              if (deptObj) fetchDesignations(deptObj.id);
            }}
          >
            {departments.map((dept) => (
              <Option key={dept.id} value={dept.name}>
                {dept.name}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Designation */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            showSearch
            placeholder="Select Designation"
            style={{ width: "100%" }}
            value={designationFilter || undefined}
            allowClear
            disabled={!departmentFilter}
            onChange={(value) => setDesignationFilter(value)}
          >
            {designations.map((des) => (
              <Option key={des.id} value={des.name}>
                {des.name}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Clear Filters Button */}
        <Col xs={24} sm={12} md={6} lg={4}>
          <Button
            type="default"
            onClick={handleClearFilters}
            style={{ width: "100%" }}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* LOADER */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        /* TABLE */
        <Table
          columns={columns}
          dataSource={filteredEmployees}
          rowKey={(record) => record.employee?.id || record.id}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredEmployees.length,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          bordered
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
      )}

      {showModal && (
        <ProbationReviewModal
          visible={showModal}
          employee={selectedEmployee}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}